import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Container,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Collapse
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  SwapHoriz as SwapHorizIcon,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../types';
import { tenantSettingsAPI, memberProfileAPI } from '../../services/api';
import { getNavigationForRole } from './navigationConstants';
import OnboardingWarningBar from '../onboarding/OnboardingWarningBar';
import OnboardingModal from '../onboarding/OnboardingModal';
import { useOnboarding } from '../../hooks/useOnboarding';
import SystemNotificationBanner from '../notifications/SystemNotificationBanner';

const drawerWidth = 240;

// Helper function to validate and return a valid color
const getValidColor = (color: string | undefined): string => {
  const defaultColor = '#1976d2'; // Material-UI primary blue
  
  if (!color || color.trim() === '') {
    return defaultColor;
  }
  
  // Basic validation for hex colors
  const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  if (hexColorRegex.test(color.trim())) {
    return color.trim();
  }
  
  // For other valid CSS colors (named colors, rgb, rgba, etc.)
  // We'll trust the browser to handle them
  return color.trim() || defaultColor;
};

const MainLayout: React.FC = () => {
  // Initialize drawer to be closed on all screens by default (since it's now an overlay)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [tenantSettings, setTenantSettings] = useState({ 
    tenant_heading_title: '', 
    tenant_background_colour: '#1976d2', // Default Material-UI primary color
    tenant_logo_img: ''
  });
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({});
  const [memberProfileCount, setMemberProfileCount] = useState<number>(0);
  const { user, logout, isAuthenticated, selectedMemberProfile, setSelectedMemberProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we should hide navigation (on select-member-profile page without a previously selected profile)
  const shouldHideNavigation = location.pathname === '/select-member-profile' && !selectedMemberProfile;

  // Onboarding state and handlers
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const {
    onboardingData,
    loading: onboardingLoading,
    needsOnboarding,
    shouldShowInstructions,
    refreshOnboardingData,
  } = useOnboarding({
    memberProfileId: selectedMemberProfile?.id,
    autoFetch: !!selectedMemberProfile?.id && (user?.role === UserRole.MEMBER || user?.role === UserRole.GUEST),
  });

  // Get navigation configuration for current user role
  const navigation = user ? getNavigationForRole(user.role) : { standaloneItems: [], sections: [] };

  const handleSectionToggle = (sectionLabel: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionLabel]: !prev[sectionLabel]
    }));
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
    navigate('/login');
  };

  const handleProfileClick = () => {
    handleUserMenuClose();
    if (user?.role === UserRole.MEMBER || user?.role === UserRole.GUEST) {
      navigate('/member-profile');
    } else {
      navigate('/profile');
    }
  };

  const handleShowOnboardingModal = () => {
    setShowOnboardingModal(true);
  };

  const handleCloseOnboardingModal = () => {
    setShowOnboardingModal(false);
  };

  // Warning dismissal removed - users must complete onboarding

  const handleSwitchUser = () => {
    handleUserMenuClose();
    // Clear the current selected member profile
    setSelectedMemberProfile(null);
    localStorage.removeItem('selectedMemberProfile');
    // Navigate to member profile selection
    navigate('/select-member-profile');
  };

  useEffect(() => {
    const fetchTenantSettings = async () => {
      if (user?.tenantId) {
        try {
          const response = await tenantSettingsAPI.getAllSettings();
          setTenantSettings(response.data);
        } catch (error) {
          console.error('Error fetching tenant settings:', error);
        }
      }
    };

    fetchTenantSettings();
  }, [user?.tenantId]);

  // Fetch member profile count for MEMBER and GUEST users
  useEffect(() => {
    const fetchMemberProfileCount = async () => {
      if (user?.id && (user.role === UserRole.MEMBER || user.role === UserRole.GUEST)) {
        try {
          const response = await memberProfileAPI.getUserMemberProfiles(user.id);
          setMemberProfileCount(response.data?.length || 0);
        } catch (error) {
          console.error('Error fetching member profiles:', error);
        }
      }
    };

    fetchMemberProfileCount();
  }, [user?.id, user?.role]);

  // Show/hide onboarding modal based on onboarding status (applies to both members and guests)
  useEffect(() => {
    const isOnboardableRole = user?.role === UserRole.MEMBER || user?.role === UserRole.GUEST;
    if (isOnboardableRole &&
        selectedMemberProfile &&
        onboardingData &&
        !onboardingLoading) {
      // Only show modal if shouldShowInstructions is true
      if (shouldShowInstructions) {
        setShowOnboardingModal(true);
      } else {
        // Hide modal if onboarding is complete
        setShowOnboardingModal(false);
      }
    }
  }, [user, selectedMemberProfile, onboardingData, shouldShowInstructions, onboardingLoading]);

  const drawer = (
    <div>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 1,
        }}
      >
        <Typography variant="h6" noWrap component="div">
          {user?.role === UserRole.SUPER_ADMIN ? 'Platform Admin' : tenantSettings.tenant_heading_title || ""}
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {/* Render standalone items first */}
        {navigation.standaloneItems.map((item) => (
          <ListItemButton
            key={item.label}
            component={RouterLink}
            to={item.path}
            onClick={() => {
              setDrawerOpen(false);
            }}
            sx={{ py: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 36, fontSize: 20 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.label} 
              primaryTypographyProps={{ fontSize: '0.875rem' }}
            />
          </ListItemButton>
        ))}

        {/* Add divider if we have both standalone items and sections */}
        {navigation.standaloneItems.length > 0 && navigation.sections.length > 0 && <Divider />}

        {/* Render collapsible sections */}
        {navigation.sections.map((section) => (
          <React.Fragment key={section.label}>
            {/* Section Header */}
            <ListItemButton 
              onClick={() => handleSectionToggle(section.label)}
              sx={{ 
                py: 0.5,
                backgroundColor: `${getValidColor(tenantSettings.tenant_background_colour)}40`
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, fontSize: 20 }}>
                {section.icon}
              </ListItemIcon>
              <ListItemText 
                primary={section.label} 
                primaryTypographyProps={{ 
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                  color: 'text.secondary'
                }}
              />
              {expandedSections[section.label] ? 
                <ExpandLess sx={{ fontSize: 20 }} /> : 
                <ExpandMore sx={{ fontSize: 20 }} />
              }
            </ListItemButton>

            {/* Section Items */}
            <Collapse in={expandedSections[section.label]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {section.items.map((item) => (
                  <ListItemButton
                    key={item.label}
                    sx={{ pl: 4, py: 0.5 }}
                    component={RouterLink}
                    to={item.path}
                    onClick={() => {
                      // Always close the drawer as it's an overlay now
                      setDrawerOpen(false);
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, fontSize: 20 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.label} 
                      primaryTypographyProps={{ fontSize: '0.85rem' }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </React.Fragment>
        ))}
      </List>
    </div>
  );

  // If not authenticated, don't render the layout, just the content
  // If on select-member-profile page without a selected profile, hide navigation
  if (shouldHideNavigation) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <CssBaseline />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Outlet />
        </Box>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Outlet />;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: '100%',
          zIndex: theme => theme.zIndex.drawer + 1, // Make sure AppBar is above the drawer
          backgroundColor: getValidColor(tenantSettings.tenant_background_colour), // Use tenant color or default
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            {tenantSettings?.tenant_logo_img ? (
              <Box
                component="img"
                src={tenantSettings.tenant_logo_img}
                alt="Tenant logo"
                sx={{ height: { xs: 28, sm: 36 }, mr: 2, display: 'block', objectFit: 'contain' }}
              />
            ) : null}
            <Typography variant="h6" noWrap component="div">
              {user?.role === UserRole.SUPER_ADMIN
                ? (user?.tenantId ? 'Viewing Club as Admin' : 'Platform Administration')
                : tenantSettings.tenant_heading_title || ''
              }
            </Typography>
          </Box>


          {user && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  size="large"
                  edge="end"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleUserMenuOpen}
                  color="inherit"
                >
                  <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                    {user.role === UserRole.MEMBER && selectedMemberProfile
                      ? `${selectedMemberProfile.firstName.charAt(0)}${selectedMemberProfile.lastName.charAt(0)}`
                      : `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`
                    }
                  </Avatar>
                </IconButton>
                <Typography variant="subtitle2" sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }}>
                  {user.role === UserRole.MEMBER && selectedMemberProfile
                    ? `${selectedMemberProfile.firstName} ${selectedMemberProfile.lastName}`
                    : `${user.firstName} ${user.lastName}`
                  }
                </Typography>
              </Box>
              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
              >
                <MenuItem onClick={handleProfileClick}>
                  <ListItemIcon>
                    <AccountCircleIcon fontSize="small" />
                  </ListItemIcon>
                  Profile
                </MenuItem>
                {user.role === UserRole.MEMBER && memberProfileCount > 1 && (
                  <MenuItem onClick={handleSwitchUser}>
                    <ListItemIcon>
                      <SwapHorizIcon fontSize="small" />
                    </ListItemIcon>
                    Switch User
                  </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>      <Box
        component="nav"
        sx={{
          flexShrink: 0,
          zIndex: theme => theme.zIndex.drawer,
        }}
      >        <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            boxShadow: 3,
          },
        }}
      >
          {drawer}
        </Drawer>
      </Box>      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: '100%',
        }}
      >
        <Toolbar /> {/* Add this to create space for the app bar */}
        
        <Container maxWidth="lg">
          <SystemNotificationBanner />
        </Container>

        {/* Onboarding Warning Bar */}
        {user?.role === UserRole.MEMBER && 
         selectedMemberProfile && 
         onboardingData && 
         needsOnboarding && 
         !onboardingLoading && (
          <OnboardingWarningBar
            onboardingData={onboardingData}
            onShowOnboarding={handleShowOnboardingModal}
            memberProfile={selectedMemberProfile}
          />
        )}
        
        <Outlet />
        
        {/* Onboarding Modal */}
        {user?.role === UserRole.MEMBER && selectedMemberProfile && (
          <OnboardingModal
            open={showOnboardingModal}
            onClose={handleCloseOnboardingModal}
            memberProfileId={selectedMemberProfile.id}
            onboardingData={onboardingData || undefined}
            onRefreshOnboardingData={refreshOnboardingData}
          />
        )}
      </Box>
    </Box>
  );
};

export default MainLayout;
